import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue';
import { useDocumentVisibility, useOnline } from '@vueuse/core';
import { v4 as uuidv4 } from 'uuid';
import { MenuImportsService } from '@/api/services/menu-imports.service';
import { StoreSetupService } from '@/api/services/store-setup.service';
import { WsServerEvents } from '@/const/websocket.consts';
import { useGtag } from '@/composition/use-gtag';
import { useSocket } from '@/composition/use-socket';
import { useUi } from '@/composition/use-ui';
import {
  applyDraftDelta,
  deltaHasChanges,
  draftHasItems,
  isItemFilter,
} from '@/domain/menu-import/menu-draft';
import type { TItemFilter } from '@/domain/menu-import/menu-draft';
import {
  getServerErrorCode,
  getServerErrorStatus,
} from '@/domain/reservations/reservation-errors';
import { menuImportErrorKey } from '@/domain/store-setup/setup-errors';
import { AlertType } from '@/interfaces/IAlert';
import type {
  IAnswerQuestionDto,
  IDraftDelta,
  IDraftDeltaResult,
  IDraftItem,
  IDraftPatchResult,
  ILiveBody,
  IMenuDraft,
  IMenuImportPage,
  IMenuImportRead,
  IMenuImportSnapshot,
  IMenuImportTurn,
  IStartReadDto,
  TDraftOp,
  TMenuImportEvent,
  TMenuImportStatus,
} from '@/interfaces/IMenuImport';
import type { IRestaurant } from '@/interfaces/IRestaurant';
import type { TSheetHeight } from '@/components/pos-v2/ui/pos-ui-sheet.utils';
import { i18n } from '@/plugins/i18n';
import type {
  IMenuImportSession,
  TMenuImportEntry,
  TMenuImportLoadState,
  TMenuImportSaveState,
  TMenuImportSnapshotMeta,
  TLiveDecision,
  TMenuImportView,
  TMenuPublishOutcome,
  TMenuSidePanelTab,
  TMenuWorkspaceMode,
} from './menu-import-context';

/** While a read or a chat turn runs, the snapshot is polled this often… */
export const MENU_IMPORT_POLL_MS = 3000;
/** …but only once no socket event arrived for this long (D20). */
export const MENU_IMPORT_POLL_SILENCE_MS = 6000;
export const MENU_IMPORT_PRESENCE_MS = 30000;
/** Waits before each retry of a save that failed without an answer (network). */
export const MENU_IMPORT_SAVE_RETRY_MS: readonly number[] = [1000, 3000, 9000];
export const MENU_IMPORT_FILTER_KEY = 'menu-import-filter';

/** Errors after which the local state is stale: the snapshot is reloaded. */
const RELOAD_CODES: ReadonlySet<string> = new Set([
  'SESSION_PUBLISHED',
  'QUESTION_CLOSED',
  'READ_RUNNING',
  'DRAFT_CHANGED',
]);

/** What the session reads of the store (an `IRestaurant` fits). */
export type TMenuImportStore = Pick<IRestaurant, 'id' | 'currency'>;

export interface IUseMenuImportSessionOptions {
  /** The store of the page; its currency is the fallback display currency. */
  restaurant: () => TMenuImportStore | null;
  entry: TMenuImportEntry;
}

/** Compared with `===` (the admin compiles without strictNullChecks, where truthiness does not narrow unions). */
type TWriteOutcome<T> =
  | { status: 'done'; value: T }
  | { status: 'error'; error: unknown };

interface IQueuedWrite {
  /** Sends the request and applies its answer; throws the request's error. */
  attempt(): Promise<void>;
  /** Settles the caller with the error (the write is dropped). */
  fail(error: unknown): void;
  /**
   * Kept at the head of the queue when the retries run out (`failed` shown),
   * or at once while the browser is offline (`offline` shown).
   */
  keepOnNetworkFailure: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringList(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((entry) => typeof entry === 'string')
  );
}

/** An axios request that got no answer at all (offline, timeout, dropped connection). */
export function isNetworkError(error: unknown): boolean {
  return (
    isRecord(error) && error.isAxiosError === true && !isRecord(error.response)
  );
}

/** Answers of a proxy in front of the API (Heroku, Cloudflare) while it restarts or is unreachable. */
const GATEWAY_STATUSES: ReadonlySet<number> = new Set([
  502,
  503,
  504,
  520,
  521,
  522,
  523,
  524,
]);

/**
 * A gateway failure: the request may not have reached the API, and the API
 * gave no answer of its own (an API error always carries a `code`, even a
 * 503 `AI_UNAVAILABLE`).
 */
export function isGatewayError(error: unknown): boolean {
  const status = getServerErrorStatus(error);
  return (
    status !== null &&
    GATEWAY_STATUSES.has(status) &&
    getServerErrorCode(error) === null
  );
}

/** Worth sending again: every queued write is idempotent (`clientMutationId`, publish replays). */
export function isRetryableWriteError(error: unknown): boolean {
  return isNetworkError(error) || isGatewayError(error);
}

/** A socket payload of `r:{id}:miU` (§5.4); anything else is ignored. */
export function isMenuImportEvent(value: unknown): value is TMenuImportEvent {
  if (!isRecord(value) || typeof value.sessionId !== 'string') return false;
  switch (value.type) {
    case 'delta':
      return isRecord(value.delta) && typeof value.delta.rev === 'number';
    case 'resync':
      return typeof value.rev === 'number';
    case 'page':
      return isRecord(value.page) && typeof value.page.pageId === 'string';
    case 'read':
      return isRecord(value.read) && typeof value.read.readNo === 'number';
    case 'turn':
      return isRecord(value.turn) && typeof value.turn.turnId === 'string';
    default:
      return false;
  }
}

/** `details` of a 422 `UNRESOLVED` publish. */
function unresolvedDetails(
  error: unknown
): { itemIds: string[]; questionIds: string[] } {
  const response = isRecord(error) ? error.response : null;
  const data = isRecord(response) ? response.data : null;
  const details = isRecord(data) ? data.details : null;
  return {
    itemIds:
      isRecord(details) && isStringList(details.itemIds) ? details.itemIds : [],
    questionIds:
      isRecord(details) && isStringList(details.questionIds)
        ? details.questionIds
        : [],
  };
}

function readStoredFilter(): TItemFilter {
  try {
    const stored = localStorage.getItem(MENU_IMPORT_FILTER_KEY);
    return isItemFilter(stored) ? stored : 'all';
  } catch {
    return 'all';
  }
}

function storeFilter(filter: TItemFilter): void {
  try {
    localStorage.setItem(MENU_IMPORT_FILTER_KEY, filter);
  } catch {
    // Remembering the chip is a convenience only.
  }
}

function isFinalRead(read: IMenuImportRead): boolean {
  return read.status !== 'running';
}

/**
 * The menu import session of one store (plan §10.4, D20): loads it, keeps it
 * in sync (socket deltas in `rev` order, snapshot refetch on a gap or a
 * resync, polling while a read or a chat turn runs and the socket is
 * silent), and writes through one serial queue that retries network failures
 * (1 s, 3 s, 9 s) before asking the owner to retry; while the browser is
 * offline, the queue waits for the network instead.
 */
export function useMenuImportSession(
  options: IUseMenuImportSessionOptions
): IMenuImportSession {
  const { restaurant, entry } = options;
  const socket = useSocket();
  const ui = useUi();
  const gtag = useGtag();
  const online = useOnline();
  const visibility = useDocumentVisibility();
  const t = (key: string) => String(i18n.t(key));

  //#region State
  const loadState = ref<TMenuImportLoadState>('idle');
  const snapshotMeta = shallowRef<TMenuImportSnapshotMeta | null>(null);
  const draft = shallowRef<IMenuDraft | null>(null);
  const rev = ref(0);
  const status = ref<TMenuImportStatus | null>(null);
  const pages = shallowRef<IMenuImportPage[]>([]);
  const read = shallowRef<IMenuImportRead | null>(null);
  const turns = shallowRef<IMenuImportTurn[]>([]);
  const canUndo = ref(false);
  const aiEnabled = ref(true);
  const existingItems = ref(0);
  /** A refresh of the live menu without an owner decision runs (the pre-publish check). */
  const liveRefreshing = ref(false);
  /** Refreshes without a decision running (the load's and the pre-publish one may overlap). */
  let liveRefreshes = 0;
  const saveState = ref<TMenuImportSaveState>('saved');
  const pending = ref(0);
  const view = ref<TMenuImportView>('source');
  const manual = ref(false);
  const openItemId = ref<string | null>(null);
  const selectedItemId = ref<string | null>(null);
  const filter = ref<TItemFilter>(readStoredFilter());
  const mode = ref<TMenuWorkspaceMode>('list');
  const sidePanelTab = ref<TMenuSidePanelTab>('chat');
  const sheetOpen = ref(false);
  const sheetHeight = ref<TSheetHeight>('md');
  const viewerPageId = ref<string | null>(null);

  const sessionId = computed(() => snapshotMeta.value?.sessionId ?? null);
  const busy = computed(() => pending.value > 0);
  const displayCurrency = computed(
    () => draft.value?.currency ?? restaurant()?.currency ?? ''
  );

  /** Bumped when the store changes and on unmount: late answers of older work are dropped. */
  let epoch = 0;
  let disposed = false;
  let lastEventAt = Date.now();
  let firstItemWatch: { readNo: number; startedAt: number } | null = null;
  const reportedReads = new Set<number>();
  let reloadedForReadNo: number | null = null;
  let forceFullSnapshot = false;
  /** An event arrived before the session was known (its snapshot may predate it). */
  let missedEvents = false;

  function hold(): () => void {
    pending.value++;
    let held = true;
    return () => {
      if (!held) return;
      held = false;
      pending.value = Math.max(0, pending.value - 1);
    };
  }
  //#endregion

  //#region Errors
  /** Toast for a failed call (unless unmounted), plus its side effects. */
  /**
   * A write refused or failed: its effects on the session (the AI off, a
   * reload, published elsewhere), then the toast unless `silent`.
   */
  function reportError(
    error: unknown,
    fallbackKey: string,
    { silent = false }: { silent?: boolean } = {}
  ): void {
    const code = getServerErrorCode(error);
    const httpStatus = getServerErrorStatus(error);
    if (httpStatus === null) console.error(error);
    if (disposed) return;
    if (code === 'AI_UNAVAILABLE') aiEnabled.value = false;
    if (code !== null && RELOAD_CODES.has(code)) void refresh();
    // Published from another device or tab: the menu flow leaves the workspace with its own notice.
    if (code === 'SESSION_PUBLISHED') {
      status.value = 'published';
      canUndo.value = false;
      return;
    }
    if (silent) return;
    ui.showToast(
      AlertType.Error,
      t(menuImportErrorKey(code, httpStatus) ?? fallbackKey)
    );
  }

  async function run<T>(
    task: () => Promise<T>,
    fallbackKey: string
  ): Promise<T | null> {
    const release = hold();
    try {
      return await task();
    } catch (error) {
      reportError(error, fallbackKey);
      return null;
    } finally {
      release();
    }
  }
  //#endregion

  //#region Applying server state
  function noteFirstItem(items: IDraftItem[]): void {
    const watching = firstItemWatch;
    if (!watching || !items.some((item) => item.readNo === watching.readNo))
      return;
    firstItemWatch = null;
    gtag.event('menu_first_item_shown', {
      ms: Date.now() - watching.startedAt,
    });
  }

  /**
   * The §5.4 client rule: the next rev is applied, an older one ignored, a
   * gap refetches. A delta without changes (a write that changed nothing
   * answers the server's current rev) is never applied as a new rev: ahead
   * of the local rev it refetches (the changes of that rev are elsewhere),
   * at the local rev it only updates `canUndo` and the status. Resolves once
   * a refetch it started is done.
   */
  function applyDelta(delta: IDraftDelta): Promise<void> {
    const current = draft.value;
    if (!current) return refresh();
    if (!deltaHasChanges(delta)) {
      if (delta.rev > rev.value) return refresh();
      if (delta.rev === rev.value) {
        canUndo.value = delta.canUndo;
        status.value = delta.status;
      }
      return Promise.resolve();
    }
    if (delta.rev <= rev.value) return Promise.resolve();
    if (delta.rev !== rev.value + 1) return refresh();
    draft.value = applyDraftDelta(current, delta);
    rev.value = delta.rev;
    canUndo.value = delta.canUndo;
    status.value = delta.status;
    noteFirstItem(delta.items ?? []);
    return Promise.resolve();
  }

  function reportReadDone(done: IMenuImportRead): void {
    if (reportedReads.has(done.readNo)) return;
    reportedReads.add(done.readNo);
    const started = Date.parse(done.startedAt);
    const finished = done.finishedAt ? Date.parse(done.finishedAt) : NaN;
    gtag.event('menu_read_done', {
      status: done.status,
      items: done.itemsFound,
      ms: Number.isFinite(finished - started) ? finished - started : null,
      reason: done.reason,
    });
  }

  function setRead(next: IMenuImportRead | null, fromEvent: boolean): void {
    const previous = read.value;
    read.value = next;
    if (!next) return;
    if (!isFinalRead(next)) {
      if (fromEvent && status.value !== 'published') status.value = 'reading';
      return;
    }
    if (
      previous &&
      previous.readNo === next.readNo &&
      previous.status === 'running'
    )
      reportReadDone(next);
    // The end of a read changes the status and the pages without a new rev.
    if (fromEvent && reloadedForReadNo !== next.readNo) {
      reloadedForReadNo = next.readNo;
      void refresh();
    }
  }

  function upsertPage(page: IMenuImportPage): void {
    const next = pages.value.filter((known) => known.pageId !== page.pageId);
    next.push(page);
    next.sort((a, b) => a.position - b.position);
    pages.value = next;
  }

  function removePage(pageId: string): void {
    pages.value = pages.value.filter((page) => page.pageId !== pageId);
  }

  function upsertTurn(turn: IMenuImportTurn): void {
    const index = turns.value.findIndex(
      (known) => known.turnId === turn.turnId
    );
    const next = turns.value.slice();
    if (index < 0) next.push(turn);
    else next[index] = turn;
    turns.value = next;
  }

  function applySnapshot(snapshot: IMenuImportSnapshot): void {
    const sameSession = snapshotMeta.value?.sessionId === snapshot.sessionId;
    snapshotMeta.value = {
      sessionId: snapshot.sessionId,
      restaurantId: snapshot.restaurantId,
      kind: snapshot.kind,
      sourceRestaurantName: snapshot.sourceRestaurantName,
      limits: snapshot.limits,
    };
    if (!sameSession || !draft.value) {
      draft.value = snapshot.draft;
      rev.value = snapshot.rev;
      status.value = snapshot.status;
      canUndo.value = snapshot.canUndo;
      // A snapshot without its draft cannot seed a new local state.
      if (!snapshot.draft) forceFullSnapshot = true;
    } else if (snapshot.rev >= rev.value) {
      if (snapshot.draft) draft.value = snapshot.draft;
      else if (snapshot.rev > rev.value) forceFullSnapshot = true;
      rev.value = snapshot.rev;
      status.value = snapshot.status;
      canUndo.value = snapshot.canUndo;
    }
    pages.value = snapshot.pages;
    turns.value = snapshot.turns;
    aiEnabled.value = snapshot.aiEnabled;
    existingItems.value = snapshot.existingItems;
    setRead(snapshot.read, false);
    if (draft.value) noteFirstItem(Object.values(draft.value.items));
    // Events that arrived while the session was being loaded or created.
    const missed = !sameSession && missedEvents;
    missedEvents = false;
    if (forceFullSnapshot || missed) void refresh();
  }
  //#endregion

  //#region Snapshot refetch
  let refreshing: Promise<void> | null = null;
  let refreshQueued = false;

  async function fetchSnapshot(fromEpoch: number): Promise<void> {
    const sid = sessionId.value;
    if (!sid) return;
    const full = forceFullSnapshot || !draft.value;
    forceFullSnapshot = false;
    try {
      const snapshot = await MenuImportsService.snapshot(
        sid,
        full ? undefined : rev.value
      );
      if (disposed || fromEpoch !== epoch) return;
      applySnapshot(snapshot);
    } catch (error) {
      if (full) forceFullSnapshot = true;
      // Polling, the socket or the next action catch up; nothing to tell the owner.
      console.warn('[menu-import] snapshot refresh failed', error);
    }
  }

  /** One refetch at a time; calls while one runs trigger exactly one more. */
  function refresh(): Promise<void> {
    if (refreshing) {
      refreshQueued = true;
      return refreshing;
    }
    const fromEpoch = epoch;
    const job = (async () => {
      do {
        refreshQueued = false;
        await fetchSnapshot(fromEpoch);
      } while (refreshQueued && !disposed && fromEpoch === epoch);
    })();
    refreshing = job;
    void job.then(() => {
      if (refreshing === job) refreshing = null;
    });
    return job;
  }
  //#endregion

  //#region Serial write queue
  const queue: IQueuedWrite[] = [];
  let pumping = false;
  let paused = false;
  let retryAttempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let wakeRetry: (() => void) | null = null;

  function waitForRetry(ms: number): Promise<void> {
    return new Promise((resolve) => {
      wakeRetry = () => {
        if (retryTimer !== null) clearTimeout(retryTimer);
        retryTimer = null;
        wakeRetry = null;
        resolve();
      };
      retryTimer = setTimeout(() => wakeRetry?.(), ms);
    });
  }

  async function pumpQueue(): Promise<void> {
    if (pumping || paused) return;
    pumping = true;
    const fromEpoch = epoch;
    try {
      while (queue.length > 0 && !paused && fromEpoch === epoch) {
        const write = queue[0];
        saveState.value =
          retryAttempt > 0 || !online.value ? 'offline' : 'saving';
        try {
          await write.attempt();
          if (fromEpoch !== epoch) return;
          queue.shift();
          retryAttempt = 0;
        } catch (error) {
          if (fromEpoch !== epoch) return;
          if (!isRetryableWriteError(error)) {
            queue.shift();
            retryAttempt = 0;
            write.fail(error);
            continue;
          }
          // Offline, a kept write waits for the network without using up its
          // retries: the `online` watcher sends it again (§14.4 offline copy).
          if (write.keepOnNetworkFailure && !online.value) {
            retryAttempt = 0;
            paused = true;
            saveState.value = 'offline';
            return;
          }
          if (retryAttempt < MENU_IMPORT_SAVE_RETRY_MS.length) {
            saveState.value = 'offline';
            await waitForRetry(MENU_IMPORT_SAVE_RETRY_MS[retryAttempt++]);
            continue;
          }
          retryAttempt = 0;
          if (write.keepOnNetworkFailure) {
            paused = true;
            saveState.value = 'failed';
            return;
          }
          queue.shift();
          write.fail(error);
        }
      }
      if (fromEpoch === epoch && queue.length === 0) saveState.value = 'saved';
    } finally {
      if (fromEpoch === epoch) pumping = false;
    }
  }

  /**
   * Queues one write. `send` performs the request and applies its answer
   * (before the next write reads `rev`); the promise settles once the write
   * succeeded, failed with an answer, or ran out of retries unkept. A new
   * write while the queue waits for «Reintentar» retries the waiting ones
   * first (in order): the owner never has to find the button to go on.
   */
  function enqueueWrite<T>(
    send: () => Promise<T>,
    keepOnNetworkFailure: boolean
  ): Promise<TWriteOutcome<T>> {
    return new Promise((resolve) => {
      const release = hold();
      queue.push({
        keepOnNetworkFailure,
        attempt: async () => {
          const value = await send();
          release();
          resolve({ status: 'done', value });
        },
        fail: (error) => {
          release();
          resolve({ status: 'error', error });
        },
      });
      if (paused) {
        paused = false;
        retryAttempt = 0;
      }
      void pumpQueue();
    });
  }

  function retrySaves(): void {
    if (wakeRetry) {
      wakeRetry();
      return;
    }
    if (!paused) return;
    paused = false;
    retryAttempt = 0;
    void pumpQueue();
  }

  function dropQueue(): void {
    const dropped = queue.splice(0);
    // Lets a pump waiting for a retry end (it sees the new epoch and stops).
    wakeRetry?.();
    pumping = false;
    paused = false;
    retryAttempt = 0;
    dropped.forEach((write) => write.fail(new Error('menu import closed')));
  }

  /**
   * The answer of a queued write (PATCH, answer, apply, undo). A replayed one
   * (an earlier attempt of the same `clientMutationId` was applied but its
   * answer was lost) carries no changes: its delta already arrived by
   * socket, or the snapshot fetches it now. The queue waits for any
   * catch-up, so the next write reads the right `rev`.
   */
  async function applyWriteResult(
    result: IDraftPatchResult | IDraftDeltaResult
  ): Promise<void> {
    if (result.replayed !== true) {
      await applyDelta(result.delta);
      return;
    }
    if (result.delta.rev > rev.value) await refresh();
  }

  /** A write's outcome for its caller: its value, or null after the error toast. */
  function settleWrite<T>(
    outcome: TWriteOutcome<T>,
    fromEpoch: number
  ): T | null {
    if (outcome.status === 'done') return outcome.value;
    if (fromEpoch === epoch)
      reportError(outcome.error, 'menuImport.errors.saveFailed');
    return null;
  }

  // Every queued write carries one `clientMutationId` (uuid v4), reused on each
  // of its retries: a retry whose first attempt was applied is answered
  // `replayed` instead of being applied twice (§8.5 idempotent retries).

  function mutate(ops: TDraftOp[]): Promise<IDraftPatchResult | null> {
    const sid = sessionId.value;
    if (!sid || ops.length === 0) return Promise.resolve(null);
    const fromEpoch = epoch;
    const clientMutationId = uuidv4();
    return enqueueWrite(async () => {
      const result = await MenuImportsService.patchDraft(
        sid,
        ops,
        clientMutationId
      );
      if (fromEpoch === epoch) await applyWriteResult(result);
      return result;
    }, true).then((outcome) => settleWrite(outcome, fromEpoch));
  }

  function answer(
    questionId: string,
    body: IAnswerQuestionDto
  ): Promise<IDraftDelta | null> {
    const sid = sessionId.value;
    if (!sid) return Promise.resolve(null);
    const fromEpoch = epoch;
    const clientMutationId = uuidv4();
    return enqueueWrite(async () => {
      const result = await MenuImportsService.answer(
        sid,
        questionId,
        body,
        clientMutationId
      );
      if (fromEpoch === epoch) await applyWriteResult(result);
      return result.delta;
    }, true).then((outcome) => settleWrite(outcome, fromEpoch));
  }

  function apply(
    turnId: string,
    proposalId: string
  ): Promise<IDraftDelta | null> {
    const sid = sessionId.value;
    if (!sid) return Promise.resolve(null);
    const fromEpoch = epoch;
    const clientMutationId = uuidv4();
    return enqueueWrite(async () => {
      const result = await MenuImportsService.applyProposal(
        sid,
        turnId,
        proposalId,
        clientMutationId
      );
      if (fromEpoch === epoch) await applyWriteResult(result);
      return result.delta;
    }, true).then((outcome) => settleWrite(outcome, fromEpoch));
  }

  /**
   * «Deshacer». With `expectedRev` (a toast's «Deshacer»), only the change
   * that toast announced is undone: when anything changed the draft since,
   * nothing is sent and the owner reads «Ya no se puede deshacer ese cambio».
   */
  async function undo(expectedRev?: number): Promise<boolean> {
    const sid = sessionId.value;
    if (!sid) return false;
    const fromEpoch = epoch;
    const clientMutationId = uuidv4();
    /** The rev of the first attempt, reused by its retries (null until sent). */
    let sentRev: number | null = null;
    const outcome = await enqueueWrite(async (): Promise<boolean> => {
      if (sentRev === null) {
        // The rev at send time: the writes queued before this one are applied.
        if (expectedRev !== undefined && rev.value !== expectedRev)
          return false;
        sentRev = rev.value;
      }
      const result = await MenuImportsService.undo(
        sid,
        sentRev,
        clientMutationId
      );
      if (fromEpoch === epoch) await applyWriteResult(result);
      return true;
    }, true);
    if (fromEpoch !== epoch) return false;
    if (outcome.status === 'error') {
      if (getServerErrorCode(outcome.error) === 'DRAFT_CHANGED') {
        // Another change came first (another device, the reader): not this undo's.
        void refresh();
        if (!disposed)
          ui.showToast(AlertType.Error, t('menuImport.errors.undoExpired'));
      } else {
        reportError(outcome.error, 'menuImport.errors.saveFailed');
      }
      return false;
    }
    if (!outcome.value) {
      if (!disposed)
        ui.showToast(AlertType.Error, t('menuImport.errors.undoExpired'));
      return false;
    }
    if (!disposed) ui.showToast(AlertType.Success, t('menuImport.undone'));
    gtag.event('menu_undo', {});
    return true;
  }

  /**
   * An update session: re-reads the store's menu and compares the draft
   * again (`decision` absent), or applies one owner decision first (a link,
   * «Es un plato nuevo», others to hide; idempotent with its
   * `clientMutationId` and kept through network failures like any owner
   * write). A refresh without a decision is not kept: the caller asks again.
   * `silent`: no toast (the first check of a loaded session). false when it
   * failed.
   */
  async function refreshLive(
    decision?: TLiveDecision,
    options: { silent?: boolean } = {}
  ): Promise<boolean> {
    const sid = sessionId.value;
    if (!sid) return false;
    const fromEpoch = epoch;
    const body: ILiveBody = decision
      ? { ...decision, clientMutationId: uuidv4() }
      : {};
    if (!decision) {
      liveRefreshes += 1;
      liveRefreshing.value = true;
    }
    try {
      const outcome = await enqueueWrite(async () => {
        const result = await MenuImportsService.live(sid, body);
        if (fromEpoch === epoch) await applyWriteResult(result);
        return result;
      }, !!decision);
      if (fromEpoch !== epoch) return false;
      if (outcome.status === 'done') return true;
      if (getServerErrorCode(outcome.error) === 'READ_RUNNING') {
        void refresh();
        return false;
      }
      if (options.silent) console.error(outcome.error);
      reportError(
        outcome.error,
        decision
          ? 'menuImport.errors.saveFailed'
          : 'menuImport.update.refreshFailed',
        { silent: options.silent === true }
      );
      return false;
    } finally {
      if (!decision && fromEpoch === epoch) {
        liveRefreshes = Math.max(0, liveRefreshes - 1);
        liveRefreshing.value = liveRefreshes > 0;
      }
    }
  }

  async function publish(): Promise<TMenuPublishOutcome> {
    const sid = sessionId.value;
    if (!sid) return { result: 'failed' };
    const fromEpoch = epoch;
    // Not kept after the retries: the dialog shows its own error and «Publicar» again.
    const outcome = await enqueueWrite(
      () => MenuImportsService.publish(sid, { rev: rev.value }),
      false
    );
    if (fromEpoch !== epoch) return { result: 'failed' };
    if (outcome.status === 'done') {
      status.value = 'published';
      canUndo.value = false;
      return { result: 'published', response: outcome.value };
    }
    const code = getServerErrorCode(outcome.error);
    if (getServerErrorStatus(outcome.error) === null)
      console.error(outcome.error);
    switch (code) {
      case 'DRAFT_CHANGED':
        void refresh();
        return { result: 'changed' };
      case 'LIVE_CHANGED':
        // The menu changed since the owner looked: compare again.
        void refreshLive();
        return { result: 'changed' };
      case 'UNRESOLVED':
        return { result: 'unresolved', ...unresolvedDetails(outcome.error) };
      case 'NOTHING_VISIBLE':
        return { result: 'nothingVisible' };
      case 'READ_RUNNING':
        void refresh();
        return { result: 'failed' };
      default:
        return { result: 'failed' };
    }
  }
  //#endregion

  //#region Loading and actions
  function initialView(): TMenuImportView {
    const current = draft.value;
    if (status.value === 'reading' || status.value === 'review')
      return 'workspace';
    // A manual draft with categories and no dish yet stays in the workspace.
    if (current && current.categoryOrder.length > 0) return 'workspace';
    // A failed read keeps its «Intentar de nuevo» after a reload.
    if (read.value?.status === 'failed' && !draftHasItems(current))
      return 'workspace';
    return 'source';
  }

  async function load(): Promise<boolean> {
    const restaurantId = restaurant()?.id;
    if (!restaurantId) return false;
    const fromEpoch = epoch;
    loadState.value = 'loading';
    try {
      // A first visit of /menu/import has no session yet: the store's
      // dishes decide the copy (asked alongside, never after).
      const existing =
        entry === 'import'
          ? storeVisibleItems(restaurantId)
          : Promise.resolve(null);
      const snapshot = await MenuImportsService.active(restaurantId);
      if (disposed || fromEpoch !== epoch) return false;
      if (snapshot) applySnapshot(snapshot);
      else {
        const count = await existing;
        if (disposed || fromEpoch !== epoch) return false;
        if (count !== null) existingItems.value = count;
      }
      view.value = initialView();
      loadState.value = 'ready';
      // The menu may have changed since this draft was compared with it.
      if (
        status.value === 'review' &&
        (!!draft.value?.live || existingItems.value > 0)
      )
        void refreshLive(undefined, { silent: true });
      return true;
    } catch (error) {
      if (disposed || fromEpoch !== epoch) return false;
      console.error(error);
      loadState.value = 'error';
      return false;
    }
  }

  /** The store's visible dishes (before any session exists); null when unknown (logged). */
  function storeVisibleItems(restaurantId: number): Promise<number | null> {
    return StoreSetupService.status(restaurantId).then(
      (setup) => setup.menu.visibleItems,
      (error: unknown) => {
        console.error(error);
        return null;
      }
    );
  }

  let creating: Promise<string | null> | null = null;

  async function createSession(): Promise<string | null> {
    const restaurantId = restaurant()?.id;
    if (!restaurantId) return null;
    const fromEpoch = epoch;
    const snapshot = await run(
      () => MenuImportsService.create({ restaurantId, uiLocale: i18n.locale }),
      'menuImport.errors.generic'
    );
    if (!snapshot || disposed || fromEpoch !== epoch) return null;
    applySnapshot(snapshot);
    return snapshot.sessionId;
  }

  function ensureSession(): Promise<string | null> {
    const existing = sessionId.value;
    if (existing) return Promise.resolve(existing);
    if (!creating) {
      const job = createSession();
      creating = job;
      void job.then(() => {
        if (creating === job) creating = null;
      });
    }
    return creating;
  }

  async function beginRead(body: IStartReadDto): Promise<boolean> {
    const sid = sessionId.value;
    if (!sid) return false;
    const fromEpoch = epoch;
    const started = await run(
      () => MenuImportsService.startRead(sid, body),
      'menuImport.errors.generic'
    );
    if (!started || disposed || fromEpoch !== epoch) return false;
    firstItemWatch = { readNo: started.readNo, startedAt: Date.now() };
    status.value = 'reading';
    canUndo.value = false;
    setRead(started, false);
    view.value = 'workspace';
    // The pages of the read change status without a socket event of their own.
    void refresh();
    return true;
  }

  function startRead(note: string | null): Promise<boolean> {
    const trimmed = note?.trim() ?? '';
    return beginRead({ note: trimmed || null });
  }

  function retryRead(readNo: number): Promise<boolean> {
    return beginRead({ retryReadNo: readNo });
  }

  async function cancelRead(): Promise<boolean> {
    const sid = sessionId.value;
    if (!sid) return false;
    const fromEpoch = epoch;
    const done = await run(async () => {
      await MenuImportsService.cancelRead(sid);
      return true;
    }, 'menuImport.errors.generic');
    if (!done || disposed || fromEpoch !== epoch) return false;
    void refresh();
    return true;
  }

  async function copyFrom(sourceRestaurantId: number): Promise<boolean> {
    const restaurantId = restaurant()?.id;
    if (!restaurantId) return false;
    const fromEpoch = epoch;
    const snapshot = await run(
      () =>
        MenuImportsService.copy({
          restaurantId,
          sourceRestaurantId,
          uiLocale: i18n.locale,
        }),
      'menuImport.errors.generic'
    );
    if (!snapshot || disposed || fromEpoch !== epoch) return false;
    applySnapshot(snapshot);
    view.value = 'workspace';
    return true;
  }

  async function startManual(): Promise<boolean> {
    const fromEpoch = epoch;
    const sid = await ensureSession();
    if (!sid || disposed || fromEpoch !== epoch) return false;
    manual.value = true;
    view.value = 'workspace';
    return true;
  }
  //#endregion

  //#region Realtime, polling, presence
  function onEvent(payload: unknown): void {
    if (!isMenuImportEvent(payload)) return;
    const sid = sessionId.value;
    if (!sid) {
      missedEvents = true;
      return;
    }
    if (payload.sessionId !== sid) return;
    lastEventAt = Date.now();
    switch (payload.type) {
      case 'delta':
        void applyDelta(payload.delta);
        break;
      case 'resync':
        void refresh();
        break;
      case 'page':
        upsertPage(payload.page);
        break;
      case 'read':
        setRead(payload.read, true);
        break;
      case 'turn':
        upsertTurn(payload.turn);
        break;
    }
  }

  let detachSocket: () => void = () => undefined;
  watch(
    [socket.socket, () => restaurant()?.id ?? null],
    ([current, restaurantId]) => {
      detachSocket();
      detachSocket = () => undefined;
      if (!current || !restaurantId) return;
      const event = `r:${restaurantId}:${WsServerEvents.MENU_IMPORT}`;
      current.on(event, onEvent);
      detachSocket = () => current.removeListener(event, onEvent);
    },
    { immediate: true, flush: 'sync' }
  );

  // The session's events only reach the screens that joined its room: join
  // it once connected, again after every reconnect (the server forgets
  // rooms), and leave it for another session or on leaving.
  let roomSessionId: string | null = null;
  function leaveSessionRoom(): void {
    const current = socket.socket.value;
    if (roomSessionId !== null && current?.connected)
      current.emit('lMI', { s: roomSessionId });
    roomSessionId = null;
  }
  watch(
    [socket.socket, socket.isConnected, sessionId],
    ([current, connected, sid]) => {
      if (roomSessionId !== null && roomSessionId !== sid) leaveSessionRoom();
      if (!current || !connected || !sid) return;
      current.emit('jMI', { s: sid });
      roomSessionId = sid;
    },
    { immediate: true }
  );

  // Events missed while disconnected are recovered from the snapshot.
  watch(socket.isConnected, (connected, wasConnected) => {
    if (connected && !wasConnected && sessionId.value) void refresh();
  });

  const shouldPoll = computed(
    () =>
      !!sessionId.value &&
      (status.value === 'reading' ||
        read.value?.status === 'running' ||
        turns.value.some((turn) => turn.status === 'thinking'))
  );
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  function stopPolling(): void {
    if (pollTimer !== null) clearInterval(pollTimer);
    pollTimer = null;
  }

  watch(
    shouldPoll,
    (poll) => {
      stopPolling();
      if (!poll) return;
      // The socket gets the full silence window before the first poll.
      lastEventAt = Date.now();
      pollTimer = setInterval(() => {
        if (Date.now() - lastEventAt >= MENU_IMPORT_POLL_SILENCE_MS)
          void refresh();
      }, MENU_IMPORT_POLL_MS);
    },
    { immediate: true }
  );

  // Tells the server someone watches (no «menu ready» email for an owner who saw the end).
  const presenceTimer = setInterval(() => {
    const sid = sessionId.value;
    if (!sid || visibility.value !== 'visible') return;
    MenuImportsService.presence(sid).catch(() => undefined);
  }, MENU_IMPORT_PRESENCE_MS);

  // Saves waiting for the network go out as soon as it is back; saves waiting
  // for «Reintentar» wait for the network too while it is gone.
  watch(online, (isOnline) => {
    if (isOnline) retrySaves();
    else if (paused) saveState.value = 'offline';
  });

  watch(filter, storeFilter);
  //#endregion

  //#region Store change and teardown
  function resetState(): void {
    epoch++;
    dropQueue();
    creating = null;
    refreshing = null;
    refreshQueued = false;
    forceFullSnapshot = false;
    firstItemWatch = null;
    reportedReads.clear();
    reloadedForReadNo = null;
    missedEvents = false;
    loadState.value = 'idle';
    snapshotMeta.value = null;
    draft.value = null;
    rev.value = 0;
    status.value = null;
    pages.value = [];
    read.value = null;
    turns.value = [];
    canUndo.value = false;
    aiEnabled.value = true;
    existingItems.value = 0;
    liveRefreshes = 0;
    liveRefreshing.value = false;
    saveState.value = 'saved';
    view.value = 'source';
    manual.value = false;
    openItemId.value = null;
    selectedItemId.value = null;
    mode.value = 'list';
    sidePanelTab.value = 'chat';
    sheetOpen.value = false;
    sheetHeight.value = 'md';
    viewerPageId.value = null;
  }

  watch(
    () => restaurant()?.id ?? null,
    (restaurantId, previousId) => {
      if (restaurantId === previousId) return;
      const wasLoaded = loadState.value !== 'idle';
      resetState();
      if (restaurantId && wasLoaded) void load();
    },
    { flush: 'sync' }
  );

  onScopeDispose(() => {
    disposed = true;
    epoch++;
    dropQueue();
    stopPolling();
    clearInterval(presenceTimer);
    detachSocket();
    leaveSessionRoom();
  });
  //#endregion

  return {
    entry,
    loadState,
    snapshotMeta,
    sessionId,
    draft,
    rev,
    status,
    pages,
    read,
    turns,
    canUndo,
    aiEnabled,
    existingItems,
    liveRefreshing,
    saveState,
    pending,
    busy,
    view,
    manual,
    openItemId,
    selectedItemId,
    filter,
    mode,
    sidePanelTab,
    sheetOpen,
    sheetHeight,
    viewerPageId,
    displayCurrency,
    load,
    refresh,
    ensureSession,
    run,
    hold,
    upsertPage,
    removePage,
    upsertTurn,
    mutate,
    answer,
    apply,
    undo,
    publish,
    refreshLive,
    retrySaves,
    startRead,
    cancelRead,
    retryRead,
    copyFrom,
    startManual,
  };
}
